import React, { useEffect, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';

import { multipleBrandsAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { makeURL, findLatestExistingBatchData } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';
import useViewportWidth from '../../../myHooks/useViewportWidth';
import { getCompetitiveSetBrandsHeaderData, mapCompetitiveSetBrandsTableData } from './chartCalculations/helpers';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';

import RecentCardExtended from '../../../myComponents/recentView/recentCardExpanded';
import Filters from '../../../myComponents/filters/Filters';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';
import Watermark from '../../../myComponents/watermark';

const CompetitiveSetBrands = () => {
  const history = useHistory();
  const appData = useSelector(({ data }) => data);
  const viewportWidth = useViewportWidth();
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [chosenFilters, setChosenFilters] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);

  const [pageIsLoading, setPageIsLoading] = useState(true);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: true,
    results: null,
  });

  useEffect(() => {
    if (!appData.loading) {
      if (appData.error) {
        showToastWarn();
      } else {
        const { latestQuarter, latestYear } = findLatestExistingBatchData(appData.existingBatchData[DEFAULT_BATCH_COUNTRY]);
        const pickedBatchToSet = {
          quarter: [latestQuarter],
          year: latestYear,
        };
        setPickedBatchData(pickedBatchToSet);
        setPickedBatchCountry(DEFAULT_BATCH_COUNTRY);

        multipleBrandsAlgorithmCalculation({
          filters: chosenFilters,
          batches: pickedBatchToSet,
          batchCountry: DEFAULT_BATCH_COUNTRY,
        })
          .then(res => {
            setCalculations({ results: res, loading: false });
            setPageIsLoading(false);
          })
          .catch(res => {
            showToastWarn();
            setPageIsLoading(false);
            setCalculations({ results: null, loading: false });
          });
      }
    }
  }, [appData]);

  const calculateHandler = (filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    multipleBrandsAlgorithmCalculation({
      filters: filters,
      batches: newPickedBatchData,
      batchCountry: newPickedBatchCountry,
    })
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        showToastWarn();
        setCalculations({ results: null, loading: false });
      });
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run brand algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.existingBatchData[value])
      .sort()
      .pop();
    const quarters = appData.existingBatchData[value][newestBatchYear].sort((a, b) => a.localeCompare(b));
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.existingBatchData[pickedBatchCountry][value].sort((a, b) => a.localeCompare(b));
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run buyer persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const viewSpecificSurveyHandler = id => {
    history.push(makeURL(`/competitive-set-brands/${id}`), { batchCountry: pickedBatchCountry, batchData: pickedBatchData });
  };

  const competitiveSetBrandsHeaderData = getCompetitiveSetBrandsHeaderData(viewportWidth);

  const competitiveSetBrandsTableData = calculations.results
    ? calculations.results.no_data
      ? []
      : mapCompetitiveSetBrandsTableData(Object.values(calculations.results), viewportWidth)
    : [];

  const showCalculations = competitiveSetBrandsTableData.length > 0;

  return (
    <Box position="relative" height="100%">
      <UserDropdownHeader bgColor="#f4f4f7" />
      <PageContainer
        headingStyle={{
          fontWeight: 'bold',
          margin: '25px 0 0 25px',
        }}
        style={{
          position: 'relative',
        }}>
        {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
        {calculations.results ? (
          <Box width="100%" margin="0px auto 50px auto">
            <Box marginLeft="10px">
              <BuyerBatchSelector
                calculationType={'batch'}
                onChangeCalculationType={() => {}}
                pickedBuyerPersona={null}
                onChangeBuyerPersona={() => {}}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                type="competitive"
                withConsumerHeading
              />
            </Box>

            <Filters
              calculateHandler={onFiltersSubmit}
              themeColor="green"
              csvExport={showCalculations ? calculations.results : false}
              pickedBatchCountry={pickedBatchCountry}
            />

            {showCalculations ? (
              <RecentCardExtended
                heading="Competitive Set Brands"
                type="competitive-brand"
                listSections={competitiveSetBrandsHeaderData}
                surveyData={competitiveSetBrandsTableData}
                viewSpecific={viewSpecificSurveyHandler}
                isLoading={appData.loading}
              />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
                <Typography
                  variant="h6"
                  color="textSecondary"
                  style={{
                    fontSize: '20px',
                    margin: '0',
                    padding: '0',
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}>
                  No data, choose other "Consumer Settings" combination.
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
        <Box width="100%" margin="30px auto">
          <ToastContainer
            theme="colored"
            position="bottom-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Box>
      </PageContainer>
      <Box position="absolute" bottom="10px" display="flex" justifyContent="center" width="100%">
        <Watermark />
      </Box>
    </Box>
  );
};

export default CompetitiveSetBrands;
